import PropTypes from 'prop-types';
import styles from './updateWalletTransaction.module.scss';
import {useMutation, useQuery} from '@apollo/client';
import {
  GET_WALLET_TRANSACTION_BY_ID,
  GET_SIM_WALLET_TRANSACTIONS,
} from 'graphql/queries/wallet';
import {Button, Form, Input, Modal, Select, Spin, Tag} from 'antd';
import {useEffect, useMemo, useState} from 'react';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {
  UPDATE_USER_WALLET_TRANSACTION,
  UPDATE_WALLET_TRANSACTION_TRANSFER_ID,
} from 'graphql/mutations/wallet';
import {TRANSLATE_TEXT} from 'graphql/mutations/notifications';
import {toast} from 'react-toastify';
import {currencySymbols} from 'utils/mock';
import {CheckOutlined, CopyOutlined, EditOutlined} from '@ant-design/icons';
import FileUploadList from 'components/fileUploadList';
import {isDev} from 'utils/constants';
import {
  OTHER_REASON_CODE,
  WALLET_FAILURE_REASONS,
  WITHDRAWAL_FAILURE_REASONS,
  resolveReasonSelection,
  buildReasonPayload,
} from 'utils/rejectionReasonRegistry';

/**
 * What a currency swap actually was, for the transaction drawer.
 *
 * The drawer used to show three numbers — swapped, received, fee — with no
 * pair and no indication of which side of the swap this transaction is. A
 * reviewer looking at one currency's ledger could not tell whether the money
 * had left for another currency or arrived from one, which is the first thing
 * they need to know.
 *
 * A swap writes one transaction into each currency's ledger: a WITHDRAWAL in
 * the source and a DEPOSIT in the target, both carrying the same swap record.
 * `direction` is the only thing that says which leg is on screen.
 */
const SwapDetails = ({tx}) => {
  const swap = tx?.swap;
  if (!swap) return null;

  const from = swap?.sourceCurrency?.currency;
  const to = swap?.targetCurrency?.currency;
  const fromSymbol = swap?.sourceCurrency?.currencySymbol ?? '';
  const toSymbol = swap?.targetCurrency?.currencySymbol ?? '';
  const incoming = tx?.direction === 'DEPOSIT';

  const money = (symbol, value) =>
    value === null || value === undefined
      ? 'N/A'
      : `${symbol}${Number(value).toLocaleString('en-US', {
          maximumFractionDigits: 6,
        })}`;

  // Only shown when it differs from the rate actually used — otherwise it is
  // three identical numbers and the one that matters gets lost.
  const rateUsed = swap?.rateUsed ?? swap?.rate;
  const marketRate = swap?.currentMarketRate;
  const pairRate = swap?.configuredPairRate;

  return (
    <div className={styles.summary}>
      <p>
        <strong>Swap:</strong>
        {from} → {to}
      </p>
      <p>
        <strong>This transaction:</strong>
        {incoming
          ? `Money arriving in ${to}, swapped from ${from}`
          : `Money leaving ${from}, swapped to ${to}`}
      </p>
      <p>
        <strong>Status:</strong>
        {swap?.status || 'N/A'}
      </p>
      <p>
        <strong>Amount Paid:</strong>
        {money(fromSymbol, swap?.amountPaid)}
      </p>
      <p>
        <strong>Amount Swapped:</strong>
        {money(fromSymbol, swap?.amountSwapped)}
      </p>
      <p>
        <strong>Amount Received:</strong>
        {money(toSymbol, swap?.amountReceived)}
      </p>
      <p>
        <strong>Rate Used:</strong>
        {rateUsed
          ? `1 ${from} = ${Number(rateUsed).toLocaleString('en-US', {
              maximumFractionDigits: 6,
            })} ${to}`
          : 'N/A'}
        {swap?.usedCustomExchangeRate ? ' (custom rate)' : ''}
      </p>
      {Boolean(pairRate) && pairRate !== rateUsed && (
        <p>
          <strong>Configured Pair Rate:</strong>
          {Number(pairRate).toLocaleString('en-US', {maximumFractionDigits: 6})}
        </p>
      )}
      {Boolean(marketRate) && marketRate !== rateUsed && (
        <p>
          <strong>Market Rate At The Time:</strong>
          {Number(marketRate).toLocaleString('en-US', {
            maximumFractionDigits: 6,
          })}
        </p>
      )}
      <p>
        <strong>Total Fee:</strong>
        {money(fromSymbol, swap?.calculatedFees)}
        {swap?.isUserDabaPro ? ' (Daba Pro)' : ''}
      </p>
      {Boolean(swap?.amountSavedInFees) && (
        <p>
          <strong>Saved In Fees:</strong>
          {money(fromSymbol, swap?.amountSavedInFees)}
          {swap?.actualFeeWithoutDiscount
            ? ` (would have been ${money(
                fromSymbol,
                swap.actualFeeWithoutDiscount,
              )})`
            : ''}
        </p>
      )}
      <p>
        <strong>Swap Reference:</strong>
        {swap?.referenceId || 'N/A'}
      </p>
      <p>
        <strong>Swap ID:</strong>
        {swap?.id || 'N/A'}
      </p>
    </div>
  );
};

SwapDetails.propTypes = {
  tx: PropTypes.object,
};

const UpdateWalletTransaction = ({
  currency,
  closeModal,
  operation,
  id,
  form,
  refetch,
  userId,
  isSimMode,
}) => {
  const [copied, setCopied] = useState('');

  const handleCopy = (referenceId, type) => {
    navigator.clipboard.writeText(referenceId).then(() => {
      setCopied(type);
      setTimeout(() => {
        setCopied('');
      }, 400);
    });
  };
  const [details, setDetails] = useState({});

  // Standalone "edit the stored transfer id" flow — independent of the
  // status-transition form above. Handy when the id was typed wrong on
  // approval, or the receipt only turned up after the fact. The server
  // moves the (provider, transferId) claim atomically for SUCCESSFUL txs,
  // so this cannot bypass the duplicate guard.
  const [editTransferIdOpen, setEditTransferIdOpen] = useState(false);
  const [editTransferIdForm] = Form.useForm();
  const [updateTransferId, {loading: updatingTransferId}] = useMutation(
    UPDATE_WALLET_TRANSACTION_TRANSFER_ID,
    {onError: err => toast.error(err.message)},
  );
  const transactionStatus = Form.useWatch('status', form);
  const shouldShowFailureReason = transactionStatus === 'FAILED';

  const {
    loading,
    data,
    refetch: refetchTransaction,
  } = useQuery(GET_WALLET_TRANSACTION_BY_ID, {
    variables: {
      transactionId: id,
      currency,
      userId,
    },
    skip: isSimMode,
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    onCompleted: data => setDetails(data?.adminGetWalletTransactionById),
    onError: error => toast.error(error.message),
  });

  const {
    loading: simLoading,
    data: simData,
    refetch: refetchSimTransaction,
  } = useQuery(GET_SIM_WALLET_TRANSACTIONS, {
    variables: {
      input: {
        currency,
        userId,
        filters: {search: id},
        pagination: {limit: 10, page: 1},
      },
    },
    skip: !isSimMode,
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    onCompleted: data =>
      setDetails(data?.getSimWalletTransactions?.transactions?.[0]),
    onError: error => toast.error(error.message),
  });

  const transactionData = isSimMode
    ? simData?.getSimWalletTransactions?.transactions?.[0]
    : data?.adminGetWalletTransactionById;
  const transactionType = transactionData?.type || details?.type;

  // Amount adjustment (mirrors the automaton's SUCCESS_ADJUST path) is only
  // meaningful for a PENDING deposit that will settle to SUCCESSFUL. Show it
  // for that case only; keep it invisible otherwise so the form isn't
  // cluttered where it can't apply.
  const originalTotalAmount = Number(
    String(transactionData?.totalTransactionAmountIncludingFees || 0).replace(
      /,/g,
      '',
    ),
  );
  const canAdjustAmount =
    transactionData?.status === 'PENDING' &&
    transactionType === 'DEPOSIT' &&
    transactionStatus === 'SUCCESSFUL';

  // One wire may only ever move once, so approving a guarded transaction
  // (WISE / REVOLUT / WIRE_TRANSFER / BANK_ACCOUNT / DWOLLA / INTERAC across
  // deposits and withdrawals) requires the transfer id from the receipt.
  // Deliberately NOT prefilled from the automation's extraction — a prefilled
  // value gets rubber-stamped, and then the check verifies nothing. The
  // extracted value is shown read-only beside the input so a mismatch is
  // visible.
  //
  // Keep the guarded list in step with users/src/utils/providerTransferId.js.
  const TRANSFER_ID_GUARDED_PROVIDERS = [
    'WISE',
    'REVOLUT',
    'WIRE_TRANSFER',
    'BANK_ACCOUNT',
    'DWOLLA',
    'INTERAC',
  ];
  // Payment methods that trip the guard even when the provider isn't in the
  // list above. Needed for WIRE_TRANSFER, which lands with
  // paymentGateway.provider = 'PLATFORM' on the user-side tx (PLATFORM is
  // shared with CRYPTO / APPLE / GOOGLE_PAY, so we can't guard PLATFORM
  // directly).
  const TRANSFER_ID_GUARDED_METHODS = ['WIRE_TRANSFER'];
  const TRANSFER_ID_GUARDED_DIRECTIONS = ['DEPOSIT', 'WITHDRAWAL'];
  const txProvider = (
    transactionData?.paymentGateway?.provider ||
    transactionData?.paymentProvider ||
    ''
  ).toUpperCase();
  const txPaymentMethod = (
    transactionData?.paymentGateway?.metadata?.paymentMethod || ''
  ).toUpperCase();
  const transferIdGuarded =
    TRANSFER_ID_GUARDED_DIRECTIONS.includes(transactionType) &&
    (TRANSFER_ID_GUARDED_PROVIDERS.includes(txProvider) ||
      TRANSFER_ID_GUARDED_METHODS.includes(txPaymentMethod));
  const requiresTransferId =
    transferIdGuarded && transactionStatus === 'SUCCESSFUL';
  const storedTransferId =
    transactionData?.paymentGateway?.metadata?.providerTransferId || null;
  const wiseExtractedTransferId =
    transactionData?.paymentGateway?.metadata?.wiseExtractedTransferId || null;
  // An id an admin already confirmed (approval, manual create or the edit
  // pencil stamp their admin id) is pre-filled, so re-approving a deposit
  // that was failed by mistake doesn't mean retyping it. Ids from the user's
  // own entry or the automation are still only hinted, never pre-filled.
  const storedTransferIdBy = String(
    transactionData?.paymentGateway?.metadata?.providerTransferIdBy || '',
  );
  const adminConfirmedTransferId =
    storedTransferId &&
    (storedTransferIdBy === 'ADMIN' ||
      /^[a-f0-9]{24}$/i.test(storedTransferIdBy))
      ? storedTransferId
      : null;
  // What to hint at beside the input. WISE has an automation-read value;
  // the other providers rely purely on the admin typing what the receipt
  // says, so only the admin-stored value is meaningful there.
  const extractedTransferId =
    txProvider === 'WISE'
      ? storedTransferId || wiseExtractedTransferId
      : storedTransferId;
  // Prefer the payment-method label when it's what identifies the rail — a
  // WIRE_TRANSFER tx should say "Wire", not "Platform"; a BANK_TRANSFER tx
  // should say "Bank", not "Dwolla".
  const titleCase = value =>
    value
      .toLowerCase()
      .replace(/(^|_)([a-z])/g, (_, sep, ch) => (sep ? ' ' : '') + ch.toUpperCase());
  const providerLabel =
    txPaymentMethod === 'WIRE_TRANSFER'
      ? 'Wire'
      : txPaymentMethod === 'BANK_TRANSFER'
      ? 'Bank'
      : txProvider === 'WIRE_TRANSFER'
      ? 'Wire'
      : txProvider === 'BANK_ACCOUNT'
      ? 'Bank'
      : txProvider === 'DWOLLA'
      ? 'Bank'
      : titleCase(txProvider);
  const directionLabel = transactionType === 'WITHDRAWAL' ? 'withdrawal' : 'deposit';
  const failureReasonRegistry = useMemo(() => {
    if (transactionType === 'DEPOSIT') return WALLET_FAILURE_REASONS;
    if (transactionType === 'WITHDRAWAL') return WITHDRAWAL_FAILURE_REASONS;
    return [];
  }, [transactionType]);

  const handleFailureReasonCodeChange = selectedCode => {
    if (!selectedCode || selectedCode === OTHER_REASON_CODE) {
      form.setFieldsValue({
        failureReasonMessageEN: '',
        failureReasonMessageFR: '',
      });
      return;
    }

    const selectedReason = failureReasonRegistry.find(
      reason => reason.code === selectedCode,
    );
    if (!selectedReason) return;

    form.setFieldsValue({
      failureReasonMessageEN: selectedReason.EN,
      failureReasonMessageFR: selectedReason.FR,
    });
  };

  const [updateUserWalletTransaction, {loading: loadingUpdate}] = useMutation(
    UPDATE_USER_WALLET_TRANSACTION,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const [translateText, {loading: loadingTranslation}] =
    useMutation(TRANSLATE_TEXT);

  const handleTranslateFailureMessage = async () => {
    const englishText = form.getFieldValue('failureReasonMessageEN');

    if (!englishText?.trim()) {
      toast.error('Please enter text in English first');
      return;
    }

    try {
      const {data: translateData} = await translateText({
        variables: {
          sourceLang: 'EN',
          targetLang: 'FR',
          sourceText: englishText,
        },
      });

      if (
        translateData?.translateText?.statusCode === 400 ||
        translateData?.translateText?.__typename === 'Error'
      ) {
        toast.error(
          translateData?.translateText?.message || 'Translation failed',
        );
        return;
      }

      form.setFieldsValue({
        failureReasonMessageFR: translateData?.translateText?.targetText || '',
      });
      toast.success('Translated Successfully');
    } catch (error) {
      toast.error('Translation failed');
    }
  };

  useEffect(() => {
    if (transactionData?.status) {
      const failureReasonPrefill = resolveReasonSelection({
        code: transactionData?.failureReasonCode,
        messages: transactionData?.failureReasonMessages,
        registry: failureReasonRegistry,
      });

      form.setFieldsValue({
        files: transactionData?.paymentGateway?.metadata?.files,
        status: transactionData?.status,
        transferId: adminConfirmedTransferId || undefined,
        failureReasonCode: failureReasonPrefill.selectedCode,
        failureReasonMessageEN: failureReasonPrefill.messageEN,
        failureReasonMessageFR: failureReasonPrefill.messageFR,
      });
    }
  }, [
    failureReasonRegistry,
    form,
    transactionData?.status,
    transactionData?.failureReasonCode,
    transactionData?.failureReasonMessages,
  ]);

  // Initialize form with existing files when data loads
  useEffect(() => {
    if (transactionData?.paymentGateway?.metadata?.files) {
      const existingFiles = transactionData.paymentGateway.metadata.files;
      // Ensure files is an array and set it to the form
      if (Array.isArray(existingFiles) && existingFiles.length > 0) {
        form.setFieldsValue({files: existingFiles});
      }
    }
  }, [transactionData, form]);

  const isLoading = loading || simLoading;

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
        <h1>Loading...</h1>
      </div>
    );
  }

  const handleUpdateTransaction = values => {
    const input = {
      currency,
      transactionId: id,
      status: values.status,
      userId: transactionData?.user?.id,
    };

    if (
      canAdjustAmount &&
      values?.newTotalAmount !== undefined &&
      values?.newTotalAmount !== null &&
      values?.newTotalAmount !== ''
    ) {
      const parsed = Number(values.newTotalAmount);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        toast.error('Enter a positive amount.');
        return;
      }
      if (
        !values?.amountAdjustmentReason ||
        !String(values.amountAdjustmentReason).trim()
      ) {
        toast.error('Please provide a reason for the amount adjustment.');
        return;
      }
      input.newTotalAmount = parsed;
      input.amountAdjustmentReason = String(
        values.amountAdjustmentReason,
      ).trim();
    }

    if (transferIdGuarded) {
      const entered = (values?.transferId || '').trim();
      if (requiresTransferId && !entered) {
        toast.error(
          `Enter the ${providerLabel} transfer id from the receipt to approve this ${directionLabel}.`,
        );
        return;
      }
      if (entered) input.transferId = entered;
    }

    if (values?.status === 'FAILED') {
      const reasonPayload = buildReasonPayload({
        selectedCode: values?.failureReasonCode,
        customMessageEN: values?.failureReasonMessageEN,
        customMessageFR: values?.failureReasonMessageFR,
        registry: failureReasonRegistry,
      });

      if (!reasonPayload) {
        toast.error('Please select a valid failure reason.');
        return;
      }

      input.failureReasonCode = reasonPayload.code;
      input.failureReasonMessages = reasonPayload.messages;
    }

    updateUserWalletTransaction({
      variables: {
        input,
      },
    })
      .then(async ({data: {updateUserWalletTransaction}}) => {
        if (updateUserWalletTransaction.__typename === 'Error') {
          toast.error(updateUserWalletTransaction.message);
        } else {
          toast.success('Settings Updated Successfully.');
          // Re-fetch this modal's own transaction query before closing so
          // the details panel re-renders with the just-stored transfer id
          // (and any other server-side changes) rather than showing the
          // pre-save snapshot for the moment before the modal disappears.
          // Fire-and-forget: refetch failures aren't blocking, and the
          // parent-list refetch below picks the change up too.
          try {
            if (isSimMode) {
              await refetchSimTransaction?.();
            } else {
              await refetchTransaction?.();
            }
          } catch (refetchError) {
            // eslint-disable-next-line no-console
            console.warn('post-update refetch failed', refetchError?.message);
          }
          refetch();
          form.resetFields();
          closeModal();
        }
      })
      .catch(error => {
        toast.error(error);
      });
  };

  return (
    <div className={styles.root}>
      <div className={styles.nav}>
        <h1> User Wallet Transaction Details</h1>
      </div>
      <div className={styles.profile}>
        <img src={transactionData?.user?.imageUrl} alt="" />
        <div
          onClick={() =>
            window.open(`/dashboard/user/${userId}/wallet-settings`, '_blank')
          }>
          <h2>
            {transactionData?.user?.firstName} {transactionData?.user?.lastName}
          </h2>
          <span>@{transactionData?.user?.username}</span>
        </div>
      </div>
      <section className={styles.content}>
        <Tag
          className={styles.tag}
          color={
            {
              'DEPOSIT': 'black',
              'WITHDRAWAL': 'default',
              'STOCK_BUY': 'magenta',
              'STOCK_SELL': 'cyan',
              'DIVIDEND_PAYOUT': 'orange',
              'DIVIDEND_REVERSAL': 'red',
              'CURRENCY_SWAP': 'geekblue',
              'VENTURE_INVESTMENT': 'purple',
              'VENTURE_FUND_INVESTMENT': 'gold',
              'BOND_INVESTMENT': 'volcano',
              'REWARDS_PAYIN': 'lime',
            }[details?.type] || 'blue'
          }>
          {details?.type?.replace(/_/g, ' ')}
        </Tag>
        <div className={styles.details}>
          <div className={styles.summary}>
            <p>
              <strong>Reference ID:</strong>
              {details?.referenceId}
              {copied === 'referenceId' ? (
                <CheckOutlined className={styles.copy} />
              ) : (
                <CopyOutlined
                  className={styles.copy}
                  onClick={() =>
                    handleCopy(details?.referenceId, 'referenceId')
                  }
                />
              )}
            </p>
            <p>
              <strong>Provider ID:</strong>
              {details?.paymentGateway?.provider === 'HUB2' ? (
                <>
                  {details?.paymentGateway?.metadata?.id}
                  {copied === 'providerId' ? (
                    <CheckOutlined className={styles.copy} />
                  ) : (
                    <CopyOutlined
                      className={styles.copy}
                      onClick={() =>
                        handleCopy(
                          details?.paymentGateway?.metadata?.id,
                          'providerId',
                        )
                      }
                    />
                  )}
                </>
              ) : details?.paymentGateway?.metadata?.transactionId ? (
                <>
                  {details?.paymentGateway?.metadata?.transactionId}
                  {copied === 'providerId' ? (
                    <CheckOutlined className={styles.copy} />
                  ) : (
                    <CopyOutlined
                      className={styles.copy}
                      onClick={() =>
                        handleCopy(
                          details?.paymentGateway?.metadata?.transactionId,
                          'providerId',
                        )
                      }
                    />
                  )}
                </>
              ) : (
                'N/A'
              )}
            </p>
            <p>
              <strong>Transaction Amount:</strong>
              <strong>
                {currencySymbols[currency]?.symbol}{' '}
                {details?.totalTransactionAmountIncludingFees != null
                  ? Number(
                      details.totalTransactionAmountIncludingFees,
                    ).toLocaleString()
                  : 'N/A'}
              </strong>
            </p>
            <p>
              <strong>Transaction Fee:</strong>
              {details?.transactionFee}
            </p>
            {/*
              Stored provider transfer id. Reads from paymentGateway.metadata,
              so the GET_WALLET_TRANSACTION_BY_ID refetch fired by the parent
              modal after save re-renders this line automatically. Copy-able
              so recon can paste it into the provider's dashboard.

              The row is rendered for every guarded transaction, even when no
              id has been stored yet — the Edit pencil is how you attach one
              after the fact (e.g. a WIRE_TRANSFER approved before this modal
              accepted a transferId). When nothing is stored the row shows
              "Not set" so the field is discoverable rather than hidden.
            */}
            {transferIdGuarded && (
              <p>
                <strong>{providerLabel} Transfer ID:</strong>
                {details?.paymentGateway?.metadata?.providerTransferId ||
                  details?.paymentGateway?.metadata?.wiseExtractedTransferId ||
                  'Not set'}
                {(details?.paymentGateway?.metadata?.providerTransferId ||
                  details?.paymentGateway?.metadata?.wiseExtractedTransferId) &&
                  (copied === 'providerTransferId' ? (
                    <CheckOutlined className={styles.copy} />
                  ) : (
                    <CopyOutlined
                      className={styles.copy}
                      onClick={() =>
                        handleCopy(
                          details?.paymentGateway?.metadata?.providerTransferId ||
                            details?.paymentGateway?.metadata?.wiseExtractedTransferId,
                          'providerTransferId',
                        )
                      }
                    />
                  ))}
                <EditOutlined
                  className={styles.copy}
                  title="Change the stored transfer id"
                  onClick={() => {
                    editTransferIdForm.resetFields();
                    editTransferIdForm.setFieldsValue({
                      transferId:
                        details?.paymentGateway?.metadata?.providerTransferId ||
                        details?.paymentGateway?.metadata?.wiseExtractedTransferId ||
                        '',
                      reason: '',
                    });
                    setEditTransferIdOpen(true);
                  }}
                />
              </p>
            )}
            {/*
              Deposits the customer settled themselves by giving their Wise
              transfer id (users-service resolveWiseTransfer). The wire was
              found on Daba's own Wise statement with the exact currency and
              amount, so no receipt review happened. The sender-name check is
              informational — someone else may fund a customer's account — so
              an unverified sender is flagged here for review, never blocked.
            */}
            {details?.paymentGateway?.metadata?.providerTransferIdBy ===
              'USER_WISE_RESOLUTION' && (
              <>
                <p>
                  <strong>Settlement:</strong>
                  <Tag color="blue">
                    Instant · found on Daba&apos;s Wise statement
                  </Tag>
                </p>
                <p>
                  <strong>Wise Sender:</strong>
                  {details?.paymentGateway?.metadata?.wiseResolution
                    ?.senderName || 'Not given by Wise'}{' '}
                  {details?.paymentGateway?.metadata?.wiseResolution
                    ?.senderVerified === false ? (
                    <Tag color="orange">
                      Sender unverified · name differs from customer
                    </Tag>
                  ) : (
                    <Tag color="green">Matches customer</Tag>
                  )}
                </p>
                {details?.paymentGateway?.metadata?.wiseResolution?.date && (
                  <p>
                    <strong>Arrived on Wise:</strong>
                    {new Date(
                      details.paymentGateway.metadata.wiseResolution.date,
                    ).toLocaleString()}
                  </p>
                )}
              </>
            )}
            {/*
              Show the stored failure reason whenever one is on the doc, not
              only while the tx is currently FAILED. A tx can be reset out of
              FAILED (e.g. PENDING → SUCCESSFUL after a corrected reason) and
              the historical reason is still useful audit — the row is
              suffixed "(historical)" to make the timing clear.
            */}
            {(details?.failureReasonCode ||
              details?.failureReasonMessages?.EN ||
              details?.failureReasonMessages?.FR) && (
              <>
                <p>
                  <strong>
                    Failure Reason Code
                    {details?.status !== 'FAILED' ? ' (historical)' : ''}:
                  </strong>
                  {details?.failureReasonCode || 'N/A'}
                </p>
                <p>
                  <strong>Failure Reason (EN):</strong>
                  {details?.failureReasonMessages?.EN || 'N/A'}
                </p>
                <p>
                  <strong>Failure Reason (FR):</strong>
                  {details?.failureReasonMessages?.FR || 'N/A'}
                </p>
              </>
            )}
          </div>

          {details?.swap && <SwapDetails tx={details} />}
          {details?.order && (
            <div className={styles.summary}>
              <p>
                <strong>Ticker:</strong>
                {details?.order?.symbol}
              </p>
              <p>
                <strong>Type:</strong>
                {details?.order?.type}
              </p>
              <p>
                <strong>Total Amount:</strong>
                {currencySymbols[details?.order?.assetCurrency]?.symbol}{' '}
                {details?.order?.price?.toLocaleString()}
              </p>
              <p>
                <strong>Fees:</strong>
                {currencySymbols[details?.order?.assetCurrency]?.symbol}{' '}
                {details?.order?.totalFee?.toLocaleString()}
              </p>
            </div>
          )}
        </div>

        {operation && (
          <Form
            onFinish={handleUpdateTransaction}
            layout="vertical"
            form={form}>
            <div className={styles.updateStatus}>
              <div className={styles.header}>
                <h1>Update Transaction Status</h1>
              </div>

              <Form.Item
                name="status"
                label="Transaction Status"
                className={styles.formField}
                rules={[
                  {
                    required: true,
                    message: 'Please select a status before updating',
                  },
                ]}>
                <Select placeholder="Select a Status">
                  <Select.Option key="SUCCESSFUL">Successful</Select.Option>
                  <Select.Option key="PENDING">Pending</Select.Option>
                  <Select.Option key="FAILED">Failed</Select.Option>
                </Select>
              </Form.Item>
              {canAdjustAmount && (
                <>
                  <div className={styles.failureMessageHeader}>
                    <span>Adjust Amount (optional)</span>
                  </div>
                  <p className={styles.helperText}>
                    Original total: <strong>{originalTotalAmount}</strong>.
                    Leave blank to settle at that amount, or enter the actual
                    amount received to mirror the automation's SUCCESS_ADJUST
                    path — the fee is preserved and the user's pending balance
                    is corrected by the delta before the wallet moves.
                  </p>
                  <Form.Item
                    name="newTotalAmount"
                    label="New Total Amount (including fees)"
                    className={styles.formField}
                    rules={[
                      ({getFieldValue}) => ({
                        validator: (_, value) => {
                          if (
                            value === undefined ||
                            value === null ||
                            value === ''
                          ) {
                            return Promise.resolve();
                          }
                          const num = Number(value);
                          if (!Number.isFinite(num) || num <= 0) {
                            return Promise.reject(
                              new Error('Enter a positive number'),
                            );
                          }
                          const reason = getFieldValue(
                            'amountAdjustmentReason',
                          );
                          if (!reason || !String(reason).trim()) {
                            return Promise.reject(
                              new Error(
                                'Reason is required whenever a new amount is provided',
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={`e.g. ${originalTotalAmount}`}
                    />
                  </Form.Item>
                  <Form.Item
                    name="amountAdjustmentReason"
                    label="Adjustment Reason"
                    className={styles.formField}
                    dependencies={['newTotalAmount']}
                    rules={[
                      ({getFieldValue}) => ({
                        validator: (_, value) => {
                          const newTotal = getFieldValue('newTotalAmount');
                          const hasNewTotal =
                            newTotal !== undefined &&
                            newTotal !== null &&
                            newTotal !== '';
                          if (!hasNewTotal) return Promise.resolve();
                          if (!value || !String(value).trim()) {
                            return Promise.reject(
                              new Error(
                                'Reason is required whenever a new amount is provided',
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}>
                    <Input.TextArea
                      placeholder="Why is the settled amount different from what the user submitted?"
                      autoSize={{minRows: 2, maxRows: 4}}
                    />
                  </Form.Item>
                </>
              )}
              {transferIdGuarded && (
                <>
                  <div className={styles.failureMessageHeader}>
                    <span>
                      {providerLabel} Transfer ID
                      {requiresTransferId ? '' : ' (optional)'}
                    </span>
                  </div>
                  <p className={styles.helperText}>
                    {requiresTransferId
                      ? `Read this from the payment receipt and type it in. One wire can only ever move once — this is what blocks a duplicate ${directionLabel}.`
                      : 'Optional when failing a transaction, but recorded if you supply it.'}
                    {txProvider === 'WISE'
                      ? extractedTransferId
                        ? ` Automation read: ${extractedTransferId}`
                        : ' Automation could not read one from the receipt.'
                      : adminConfirmedTransferId
                      ? ` Pre-filled with the id an admin already confirmed — check it against the receipt.`
                      : storedTransferId
                      ? ` Previously stored: ${storedTransferId}`
                      : ''}
                  </p>
                  <Form.Item
                    name="transferId"
                    label="Transfer ID"
                    className={styles.formField}
                    rules={[
                      {
                        required: requiresTransferId,
                        message: `Transfer id is required to approve this ${directionLabel}`,
                      },
                    ]}>
                    <Input placeholder="e.g. 2378903168" autoComplete="off" />
                  </Form.Item>
                </>
              )}
              {shouldShowFailureReason && (
                <>
                  <Form.Item
                    name="failureReasonCode"
                    label="Failure Reason Code"
                    rules={[
                      {
                        required: true,
                        message: 'Please select a failure reason code',
                      },
                    ]}
                    className={styles.formField}>
                    <Select
                      placeholder="Select failure reason code"
                      allowClear
                      showSearch
                      filterOption={true}
                      optionFilterProp="label"
                      onChange={handleFailureReasonCodeChange}>
                      {failureReasonRegistry.map(reason => (
                        <Select.Option
                          key={reason.code}
                          value={reason.code}
                          label={`${reason.code} - ${reason.label}`}>
                          {reason.code} - {reason.label}
                        </Select.Option>
                      ))}
                      <Select.Option
                        key={OTHER_REASON_CODE}
                        value={OTHER_REASON_CODE}
                        label={`${OTHER_REASON_CODE} - Other`}>
                        {OTHER_REASON_CODE} - Other
                      </Select.Option>
                    </Select>
                  </Form.Item>
                  <div className={styles.failureMessageHeader}>
                    <span>Failure Message</span>
                    <Button
                      htmlType="button"
                      size="small"
                      loading={loadingTranslation}
                      disabled={loadingTranslation}
                      onClick={handleTranslateFailureMessage}>
                      Translate
                    </Button>
                  </div>
                  <Form.Item
                    name="failureReasonMessageEN"
                    label="Failure Message (English)"
                    rules={[
                      {
                        required: true,
                        message: 'English failure message is required',
                      },
                    ]}
                    className={styles.formField}>
                    <Input.TextArea
                      placeholder="Enter the failure message in English"
                      autoSize={{minRows: 3, maxRows: 5}}
                    />
                  </Form.Item>
                  <Form.Item
                    name="failureReasonMessageFR"
                    label="Failure Message (French)"
                    rules={[
                      {
                        required: true,
                        message: 'French failure message is required',
                      },
                    ]}
                    className={styles.formField}>
                    <Input.TextArea
                      placeholder="Entrez le message d'echec en francais"
                      autoSize={{minRows: 3, maxRows: 5}}
                    />
                  </Form.Item>
                </>
              )}
              <SubmitButton
                disabled={loadingUpdate || !operation}
                label="Update Transaction"
              />
              <FileUploadList
                form={form}
                name="files"
                label="Files"
                bucket={
                  isDev
                    ? 'transaction-receipts-bucket-nonprod-02'
                    : 'transaction-receipts-bucket-01'
                }
                required={false}
                initialFiles={transactionData?.paymentGateway?.metadata?.files}
                transactionId={id}
                forType={transactionData?.swap ? 'SWAP' : 'WALLET'}
                currency={currency}
                refetch={isSimMode ? refetchSimTransaction : refetchTransaction}
              />
            </div>
          </Form>
        )}
      </section>

      <Modal
        title={`Change ${providerLabel} Transfer ID`}
        open={editTransferIdOpen}
        onCancel={() => setEditTransferIdOpen(false)}
        okText={updatingTransferId ? 'Saving…' : 'Save'}
        okButtonProps={{disabled: updatingTransferId}}
        cancelButtonProps={{disabled: updatingTransferId}}
        onOk={() => {
          editTransferIdForm
            .validateFields()
            .then(values => {
              const raw = (values.transferId ?? '').trim();
              return updateTransferId({
                variables: {
                  input: {
                    userId,
                    currency,
                    transactionId: id,
                    // Empty string clears the stored id on the server —
                    // send explicit null so the resolver's "null → clear"
                    // path fires and the metadata unset stamps run.
                    transferId: raw === '' ? null : raw,
                    reason: values.reason.trim(),
                  },
                },
              })
                .then(({data}) => {
                  const resp = data?.updateWalletTransactionTransferId;
                  if (resp?.__typename === 'Error') {
                    toast.error(resp.message);
                    return;
                  }
                  // A rejected request resolves with no data (onError has
                  // already shown why), so only a returned transaction is a
                  // success.
                  if (resp?.__typename !== 'WalletTransaction') return;
                  toast.success('Transfer id updated');
                  setEditTransferIdOpen(false);
                  // Refetch so the panel + parent list reflect the new
                  // stored value AND the appended history entry.
                  if (isSimMode) refetchSimTransaction?.();
                  else refetchTransaction?.();
                  refetch?.();
                });
            })
            .catch(() => {
              // validateFields rejects with the field errors; nothing to
              // toast, the inline messages are enough.
            });
        }}>
        <Form
          form={editTransferIdForm}
          layout="vertical"
          initialValues={{transferId: '', reason: ''}}>
          <p style={{marginTop: 0}}>
            The registry uniqueness guard still applies — an id already
            in use on another transaction will be rejected. Leave blank to
            remove the stored id (releases the claim on{' '}
            <strong>SUCCESSFUL</strong> transactions). Every change is
            recorded on the transaction's history with your admin id and
            the reason below.
          </p>
          <Form.Item
            label="New Transfer ID (blank to remove)"
            name="transferId"
            extra={
              extractedTransferId
                ? `Currently stored: ${extractedTransferId}`
                : 'Nothing stored yet.'
            }>
            <Input allowClear placeholder="e.g. 2378732506" />
          </Form.Item>
          <Form.Item
            label="Reason"
            name="reason"
            rules={[
              {
                required: true,
                message: 'A reason is required so the change is auditable',
              },
              {
                validator: (_, value) =>
                  (value ?? '').trim().length >= 3
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error('Reason must be at least 3 characters'),
                      ),
              },
            ]}>
            <Input.TextArea
              rows={3}
              placeholder="Why this id is being changed"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UpdateWalletTransaction;
